import { useState } from 'react';
import { X, Calendar, Clock } from 'lucide-react';
import { CalendarPicker } from '../ui/CalendarPicker';

interface Appointment {
  id: string;
  type: string;
  provider: string;
  date: string;
  time: string;
  notes?: string;
}

interface RescheduleAppointmentModalProps {
  appointment: Appointment;
  onClose: () => void;
  onReschedule: (appointmentId: string, newDate: string, newTime: string) => void;
}

const timeSlots = [
  '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
  '05:00 PM', '05:30 PM', '06:00 PM'
];

export function RescheduleAppointmentModal({ appointment, onClose, onReschedule }: RescheduleAppointmentModalProps) {
  const [formData, setFormData] = useState({
    date: appointment.date,
    time: appointment.time
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onReschedule(appointment.id, formData.date, formData.time);
    onClose();
  };

  // Get minimum date (today)
  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl mb-1">Reschedule Appointment</h2>
              <p className="text-sm opacity-90">{appointment.type}</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {/* Current Appointment Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-700 mb-2">Current Appointment</div>
              <div className="text-gray-900 mb-1">{appointment.provider}</div>
              <div className="text-sm text-gray-600">
                {new Date(appointment.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} at {appointment.time}
              </div>
            </div>

            {/* New Date */}
            <CalendarPicker
              label="New Date"
              required
              minDate={minDate}
              value={formData.date}
              onChange={(date) => setFormData({ ...formData, date })}
            />

            {/* New Time */}
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                New Time <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a time...</option>
                {timeSlots.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>

            {/* Info Box */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-yellow-800">
                <div className="mb-1">⚠️ Reschedule Notice</div>
                <div className="text-xs text-yellow-700">
                  Please reschedule at least 24 hours in advance when possible. A confirmation will be sent to you.
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Confirm Reschedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}